import { FC, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import Grid from "@mui/material/Grid2";
import "./index.css";
import { drawerWidth } from "../../../constants";
import TableComponent from "../../../components/Table";
import {
  useGetAllUsersQuery,
  useResetUserPasswordMutation,
  useUpdateAccountStatusMutation,
  useUpdateTokenBalanceMutation,
} from "../../../services/user";
import { errorToast, successToast } from "../../../utils/toast";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog";
import { tokenApi } from "../../../services/token";
import { resetMetaData, setUser } from "../../../rtkSlices/metaDataSlice";
import SearchBar from "./Search";
import { RootState } from "../../../store";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

const UserManagement: FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [paginationParams, setPaginationParams] = useState({
    limit: 25,
    offset: 0,
  });
  const [sortParams, setSortParams] = useState({
    column: "",
    order: "asc",
  });
  const getDefaultParams = () => {
    return `?limit=${paginationParams.limit}&offset=${paginationParams.offset}&sortBy=${sortParams.column}&sortOrder=${sortParams.order}`;
  };
  const [userParams, setUserParams] = useState(getDefaultParams());
  const users = useGetAllUsersQuery(userParams);
  const dispatch = useDispatch();
  const [allUsers, setAllUsers] = useState([]);
  const [updateTokens] = useUpdateTokenBalanceMutation();
  const [updateAccountStatus] = useUpdateAccountStatusMutation();
  const [resetUserPassword] = useResetUserPasswordMutation();
  const metaData = useSelector((state: RootState) => state.metaData);
  const username = metaData.userInfo["cognito:username"];
  const [statusCount, setStatusCount] = useState({ active: 0, inactive: 0 });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationProps, setConfirmationProps] = useState<any>({
    title: "",
    content: "",
    btn1: "",
    btn2: "",
    handleBtn1: () => {},
    handleBtn2: () => {},
    icon: <div></div>,
    tooltip: "",
    bgcolor: "",
  });
  const defaultDialogProps = () => {
    return {
      title: "",
      content: "",
      btn1: "",
      btn2: "",
      handleBtn1: () => {},
      handleBtn2: () => {},
      actions: true,
      icon: <div></div>,
      bgcolor: "",
    };
  };
  let tempConfirmationProps = defaultDialogProps();

  function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    return typeof error === "object" && error !== null && "status" in error;
  }

  useEffect(() => {
    if (!users.isFetching && users.isSuccess) {
      if (users.data && users.data && users.data.users) {
        setAllUsers(JSON.parse(JSON.stringify(users.data.users)));
        setStatusCount({
          active: users.data.totalActive,
          inactive: users.data.totalInactive,
        });
      }
    }
    if (!users.isFetching && users.isError) {
      const error = users.error;
      if (isFetchBaseQueryError(error) && error.status === 403) {
        errorToast("Token expired");
        localStorage.removeItem("access_token");
        localStorage.removeItem("userInfo");
        dispatch(setUser({}));
        dispatch(resetMetaData());
        navigate("/signin");
      } else {
        errorToast("Error fetching users");
      }
    }
  }, [users]);

  const sortByColumn = (column: string) => {
    const selectedColumn: string | undefined = columns.find(
      (col) => col.header == column
    )?.key;
    const params = { ...sortParams };
    params.order =
      params.column == selectedColumn
        ? params.order == "asc"
          ? "desc"
          : "asc"
        : "asc";
    if (selectedColumn) {
      params.column = selectedColumn;
    }
    setSortParams(params);
  };

  useEffect(() => {
    setUserParams(getDefaultParams());
  }, [paginationParams, sortParams]);

  const updateTokenBalance = async (
    tokenChange: number,
    currentValue: number,
    username: string
  ) => {
    const body = {
      username,
      tokenChange: tokenChange - currentValue,
      actionKey: "ManualAdjustment",
      metadata: {
        reason: "Admin adjusted token balance",
        grantedBy: username,
      },
    };
    const response: any = await updateTokens(body);
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
      tempConfirmationProps = {
        title: "WARNING",
        bgcolor: "rgba(180, 69, 52, 1)",
        content: `Are you sure you want to update token balance of this user?`,
        btn1: "Cancel",
        btn2: "Proceed",
        handleBtn1: () => setShowConfirmation(false),
        handleBtn2: () =>
          updateTokenBalanceProceed(tokenChange, currentValue, username),
        actions: true,
        icon: <div></div>,
      };
      setConfirmationProps(tempConfirmationProps);
    } else {
      const user: any = allUsers.find(
        (user: { username: string }) => user.username == username
      );
      user.tokenBalance = response.data.newBalance;
      successToast("Token balance updated successfully");
      setShowConfirmation(false);
      dispatch(tokenApi.util.invalidateTags(["tokenBalance"]));
    }
  };

  const proceedUpdateStatus = (status: string, username: string) => {
    tempConfirmationProps = {
      title: "WARNING",
      bgcolor: "rgba(180, 69, 52, 1)",
      content: `Are you sure you want to make this user status ${status}?`,
      btn1: "Cancel",
      btn2: "",
      handleBtn1: () => setShowConfirmation(false),
      handleBtn2: () => {},
      actions: true,
      icon: <div></div>,
    };
    setConfirmationProps(tempConfirmationProps);
    updateStatus(status, username);
  };

  const updateStatus = async (status: string, username: string) => {
    const body = {
      username,
      account_status: status,
    };
    const response: any = await updateAccountStatus(body);
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
      tempConfirmationProps = {
        title: "WARNING",
        bgcolor: "rgba(180, 69, 52, 1)",
        content: `Are you sure you want to make this user status ${status}?`,
        btn1: "Cancel",
        btn2: "Proceed",
        handleBtn1: () => setShowConfirmation(false),
        handleBtn2: () => proceedUpdateStatus(status, username),
        actions: true,
        icon: <div></div>,
      };
      setConfirmationProps(tempConfirmationProps);
    } else {
      successToast("User account status updated successfully");
      setShowConfirmation(false);
      const user: any = allUsers.find(
        (user: { username: string }) => user.username == username
      );
      user.accountStatus = status;
      let tempStatusCount = { ...statusCount };
      tempStatusCount.active =
        status == "Active"
          ? tempStatusCount.active + 1
          : tempStatusCount.active - 1;
      tempStatusCount.inactive =
        status == "Disabled"
          ? tempStatusCount.inactive + 1
          : tempStatusCount.inactive - 1;
      setStatusCount(tempStatusCount);
    }
  };

  const resetPassword = async (username: string) => {
    const body = {
      username,
    };
    const response: any = await resetUserPassword(body);
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
      tempConfirmationProps = {
        title: "WARNING",
        bgcolor: "rgba(180, 69, 52, 1)",
        content: "Are you sure you want to reset password of this user?",
        btn1: "Cancel",
        btn2: "Proceed",
        handleBtn1: () => setShowConfirmation(false),
        handleBtn2: () => resetPasswordProceed(username),
        actions: true,
        icon: <div></div>,
      };
      setConfirmationProps(tempConfirmationProps);
    } else {
      successToast("User password reset successfully");
      setShowConfirmation(false);
    }
  };

  const resetPasswordProceed = (username: string) => {
    tempConfirmationProps = {
      title: "WARNING",
      bgcolor: "rgba(180, 69, 52, 1)",
      content: "Are you sure you want to reset password of this user?",
      btn1: "Cancel",
      btn2: "",
      handleBtn1: () => setShowConfirmation(false),
      handleBtn2: () => {},
      actions: true,
      icon: <div></div>,
    };
    setConfirmationProps(tempConfirmationProps);
    resetPassword(username);
  };

  const actionConfirmation = (status: string, username: string) => {
    setShowConfirmation(true);
    setConfirmationProps({
      title: "WARNING",
      bgcolor: "rgba(180, 69, 52, 1)",
      content: status
        ? `Are you sure you want to make this user status ${status}?`
        : "Are you sure you want to reset password of this user?",
      btn1: "Cancel",
      btn2: "Proceed",
      handleBtn1: () => {
        setShowConfirmation(false);
      },
      actions: true,
      handleBtn2: () =>
        status
          ? proceedUpdateStatus(status, username)
          : resetPasswordProceed(username),
      icon: <div></div>,
    });
  };

  const updateTokenBalanceProceed = (
    tokenChange: number,
    currentValue: number,
    username: string
  ) => {
    tempConfirmationProps = {
      title: "WARNING",
      bgcolor: "rgba(180, 69, 52, 1)",
      content: `Are you sure you want to update token balance of this user?`,
      btn1: "Cancel",
      btn2: "",
      handleBtn1: () => setShowConfirmation(false),
      handleBtn2: () => {},
      actions: true,
      icon: <div></div>,
    };
    setConfirmationProps(tempConfirmationProps);
    updateTokenBalance(tokenChange, currentValue, username);
  };

  const tokenChangeConfirmation = (
    event: { keyCode: number; target: { value: string } },
    currentValue: number,
    username: string
  ) => {
    if (event.keyCode == 13) {
      setShowConfirmation(true);
      setConfirmationProps({
        title: "WARNING",
        bgcolor: "rgba(180, 69, 52, 1)",
        content: "Are you sure you want to update token balance of this user?",
        btn1: "Cancel",
        btn2: "Proceed",
        handleBtn1: () => {
          setShowConfirmation(false);
        },
        actions: true,
        handleBtn2: () =>
          updateTokenBalanceProceed(
            parseInt(event.target.value),
            currentValue,
            username
          ),
        icon: <div></div>,
      });
    }
  };

  const columns = [
    {
      header: "Action",
      key: "resetPassword",
      type: "button",
      bgcolor: "#30a46c",
      text: "Reset Password",
      onSubmit: actionConfirmation,
    },
    { header: "ID", key: "id", type: "string" },
    { header: "First Name", key: "firstName", type: "string", sort: true },
    { header: "Last Name", key: "lastName", type: "string", sort: true },
    { header: "Email", key: "email", type: "string", sort: true },
    { header: "Phone Number", key: "phoneNumber", type: "string" },
    {
      header: "Subscription Status",
      key: "subscriptionStatus",
      type: "string",
    },
    { header: "Payment Status", key: "paymentStatus", type: "string" },
    { header: "Next Billing Date", key: "nextBillingDate", type: "string" },
    { header: "Sign-up Date", key: "signUpDate", type: "date", sort: true },
    {
      header: "Token Balance",
      key: "tokenBalance",
      type: "input",
      bgcolor: "#c7503d",
      onSubmit: tokenChangeConfirmation,
    },
    {
      header: "Monthly Token Allotment",
      key: "monthlyTokenAllotment",
      type: "input",
      bgcolor: "#30a46c",
      disabled: true,
    },
    {
      header: "Last Login Date",
      key: "lastLoginDate",
      type: "date",
      sort: true,
    },
    {
      header: "Account Status",
      key: "accountStatus",
      type: "button",
      bgcolor: "#30a46c",
      onSubmit: actionConfirmation,
    },
    { header: "Consent Given", key: "consentGiven", type: "boolean" },
    { header: "Email Verified", key: "emailVerified", type: "boolean" },
    { header: "Notes", key: "notes", type: "string" },
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    const params: {
      limit: number;
      offset: number;
    } = { ...paginationParams };
    params.offset = newPage;
    setPaginationParams(params);
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const params: {
      limit: number;
      offset: number;
    } = { ...paginationParams };
    params.limit = parseInt(event.target.value, 10);
    params.offset = 0;
    setPaginationParams(params);
  };

  const filterUserStatus = (status: string) => {
    if (users && users.data && users.data.users) {
      let tempUsers = JSON.parse(JSON.stringify(users.data.users));
      tempUsers = tempUsers.filter(
        (user: { accountStatus: string }) => user.accountStatus == status
      );
      setAllUsers(tempUsers);
    }
  };

  const searchUsers = (value: string) => {
    setAllUsers([]);
    if (value) {
      let params = getDefaultParams();
      params =
        params +
        `&firstName=${value}&lastName=${value}&email=${value}&username=${value}`;
      setUserParams(params);
    } else {
      setUserParams(getDefaultParams());
    }
  };

  return (
    <div>
      <Box
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          marginTop: "3%",
          padding: "0px 20px",
        }}
      >
        <Grid container sx={{ width: "100%" }}>
          <Grid
            size={{ xs: 6, md: 6 }}
            display={"flex"}
            justifyContent={"center"}
          >
            <Card
              sx={{
                width: "340px",
                height: "96px",
                cursor: "pointer",
                padding: "12px",
                color:
                  mode == "dark" ? "black" : theme.palette.primary.contrastText,
                backgroundColor:
                  mode == "dark"
                    ? theme.palette.primary.contrastText
                    : theme.palette.secondary.contrastText,
              }}
              onClick={() => filterUserStatus("Active")}
            >
              <CardContent className="count-card-content">
                <div style={{ width: "70%" }}>
                  <Typography component="div" sx={{ fontSize: "24px" }}>
                    Total number of active users
                  </Typography>
                </div>
                <div className="count">{statusCount.active}</div>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{ xs: 6, md: 6 }}
            display={"flex"}
            justifyContent={"center"}
          >
            <Card
              sx={{
                width: "340px",
                height: "96px",
                cursor: "pointer",
                padding: "12px",
                color:
                  mode == "dark" ? "black" : theme.palette.primary.contrastText,
                backgroundColor:
                  mode == "dark"
                    ? theme.palette.primary.contrastText
                    : theme.palette.secondary.contrastText,
              }}
              onClick={() => filterUserStatus("Disabled")}
            >
              <CardContent className="count-card-content">
                <div style={{ width: "70%" }}>
                  <Typography component="div" sx={{ fontSize: "24px" }}>
                    Total number of inactive users
                  </Typography>
                </div>
                <div className="count">{statusCount.inactive}</div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Box
          sx={{
            border: "1px solid #30A46C",
            borderRadius: "8px",
            width: "100%",
            maxHeight: "600px",
            margin: "20px 0px",
            padding: "5px 15px",
          }}
        >
          <div className="usersinfo">
            <Typography component="div" fontFamily={"Poppins"} flex={"1"}>
              Users Information
            </Typography>
            <div className="tokensinfo">
              <Typography component="div"
                sx={{
                  fontSize: "14px",
                  fontWeight: "400 !important",
                  display: "flex",
                  gap: "2px",
                  alignItems: "center",
                }}
                fontFamily={"Poppins"}
              >
                <div className="monthly-tokens-dot"></div>
                Monthly Alotted Tokens
              </Typography>
              <Typography component="div"
                fontFamily={"Poppins"}
                sx={{
                  fontSize: "14px",
                  fontWeight: "400 !important",
                  display: "flex",
                  gap: "2px",
                  alignItems: "center",
                }}
              >
                <div className="total-tokens-dot"></div>
                Total Tokens
              </Typography>
            </div>
            <SearchBar setUserParams={searchUsers} />
          </div>
          {!allUsers.length && users.isFetching ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                margin: "10px 0px",
              }}
            >
              <CircularProgress color="secondary" />
            </div>
          ) : allUsers.length ? (
            <TableComponent
              users={allUsers}
              columns={columns}
              page={paginationParams.offset}
              rowsPerPage={paginationParams.limit}
              handleChangePage={handleChangePage}
              handleLimitChange={handleLimitChange}
              loader={users.isFetching ? true : false}
              count={statusCount.active + statusCount.inactive}
              sortByColumn={sortByColumn}
            />
          ) : users.data && users.data.users && !users.data.users.length ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                margin: "10px 0px",
              }}
            >
              <Typography component="div" className="not-found">No Users found</Typography>
            </div>
          ) : null}
        </Box>
      </Box>
      {showConfirmation ? (
        <ConfirmationDialog
          open={showConfirmation}
          content={confirmationProps.content}
          title={confirmationProps.title}
          btn1={confirmationProps.btn1}
          btn2={confirmationProps.btn2}
          handleBtn1={confirmationProps.handleBtn1}
          handleBtn2={confirmationProps.handleBtn2}
          icon={confirmationProps.icon}
          actions={confirmationProps.actions}
          tooltip={confirmationProps.tooltip}
          bgcolor={confirmationProps.bgcolor}
        />
      ) : null}
    </div>
  );
};

export default UserManagement;
